import { observer } from 'mobx-react-lite';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { SommelierButton } from '~/components/HeaderV3/components/SearchBar/components/SommelierButton';
import Icon from '~/components/Icon/Icon';

import { Category, RecommendationsItem } from '../../interfaces';

import styles from './DefaultSearchContent.module.scss';

interface DefaultSearchContentProps {
  recommendationsData: RecommendationsItem[];
  categories: Category[];
  handleSelectRecommendation: (
    value: string,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleCategoryClick: (
    categoryId: string | number,
  ) => (e: React.MouseEvent<HTMLButtonElement>) => void;
  handleHistoryReset: () => void;
}

export const DefaultSearchContent: React.FC<DefaultSearchContentProps> =
  observer(
    ({
      recommendationsData,
      categories,
      handleSelectRecommendation,
      handleCategoryClick,
      handleHistoryReset,
    }) => {
      const { t } = useTranslation();

      return (
        <div className={styles.container}>
          {recommendationsData.map((section) => (
            <div key={section.title.toString()} className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>{section.title}</h3>
                {section.action && (
                  <button
                    onClick={handleHistoryReset}
                    className={styles.clearHistory}
                  >
                    {t('header:clear')}
                  </button>
                )}
              </div>
              <div className={styles.badgesList}>
                {section.items.map((item) => (
                  <button
                    key={item}
                    className={styles.badge}
                    onClick={handleSelectRecommendation(item)}
                  >
                    <Icon
                      type={section.icon}
                      size={16}
                      className={styles.badgeIcon}
                    />
                    <span className={styles.badgeText}>{item}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {company.isSommelierEnabled && (
            <div className={styles.section}>
              <div className={styles.banner}>
                <div className={styles.bannerContent}>
                  <div className={styles.bannerText}>
                    <p>{t('header:imGenie')}</p>
                    <p>{t('header:needSomeRecomendations')}</p>
                  </div>
                  <SommelierButton
                    className={styles.sommelierButton}
                    icon="sommelier-3.1"
                  />
                </div>
              </div>
            </div>
          )}

          {categories.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h3 className={styles.sectionTitle}>
                  {t('header:categories')}
                </h3>
              </div>
              <div className={styles.categoriesList}>
                {categories.map((category) => (
                  <button
                    key={category.id}
                    className={styles.categoryCard}
                    onClick={handleCategoryClick(category.id)}
                  >
                    <div className={styles.categoryImage}>
                      <CloudflareResponseImage
                        src={category.image}
                        alt={category.name}
                        srcParams={{
                          width: 60,
                          height: 60,
                          fit: 'contain',
                        }}
                        loading="lazy"
                      />
                    </div>
                    <span className={styles.categoryName}>{category.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      );
    },
  );
